import { toSnakeCase } from '../utils/caseConverter';
import { getApiHeaders } from '../utils/apiHeaders';
import { fetchBookingDrafts, materializeBookingDraft } from './BookingDraft';

const API_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL as string;

export const fetchBookings = async (userId?: number | string) => {
  const url = new URL(`${API_BASE_URL}/bookings`);
  if (userId) {
    url.searchParams.append('user_id', String(userId));
  }
  const res = await fetch(url.toString(), {
    headers: getApiHeaders('none'),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to fetch bookings: ${res.status} ${text}`);
  }
  const data = await res.json();
  const list: any[] = Array.isArray(data)
    ? data
    : Array.isArray((data || {}).bookings)
      ? (data as any).bookings
      : [];

  const mapName = (travellers: any): string => {
    try {
      const flat = Array.isArray(travellers) ? travellers.flat(Infinity) : [];
      const lead = flat && flat.length > 0 ? flat[0] : null;
      const fn = (lead?.firstName || lead?.first_name || '').trim();
      const ln = (lead?.lastName || lead?.last_name || '').trim();
      const name = `${fn} ${ln}`.trim();
      return name || '—';
    } catch {
      return '—';
    }
  };

  const mapped = list.map((b: any) => {
    // Convert amount_paise (string) to rupees
    const amountPaiseStr = String(b.amount_paise || '0');
    const amountPaise = Number(amountPaiseStr);
    let totalPrice = 0;
    
    // Priority 1: Use payment_details.booking_amount if available (already in rupees)
    if (b.payment_details?.booking_amount !== undefined && b.payment_details.booking_amount !== null) {
      totalPrice = Number(b.payment_details.booking_amount);
    } 
    // Priority 2: Convert amount_paise from string to rupees
    else if (isFinite(amountPaise) && amountPaise > 0) {
      totalPrice = amountPaise / 100;
    } 
    // Priority 3: Fallback to pricing fields from the booking payload
    else {
      totalPrice = Number(b?.pricing?.pay) || Number(b?.pricing?.total) || 0;
    }
    
    // Extract customer information from multiple possible sources
    const customerName = mapName(b.travellers);
    const email = b?.contact?.email || b?.customer?.email || '';
    const phone = b?.contact?.phone || b?.customer?.phone || b?.customer?.phone_number || b?.customer?.phoneNumber || '';
    const customerId = b.customer_id || b.customer?.id;
    
    // Build customer object - prioritize nested customer object if available
    const customer = b.customer ? {
      id: b.customer.id ? Number(b.customer.id) : (customerId ? Number(customerId) : undefined),
      name: b.customer.name || customerName || 'Unknown',
      email: b.customer.email || email || '',
      phoneNumber: b.customer.phone_number || b.customer.phoneNumber || b.customer.phone || phone || '',
      city: b.customer.city || '',
      address: b.customer.address || '',
      profession: b.customer.profession || '',
    } : {
      id: customerId ? Number(customerId) : undefined,
      name: customerName || 'Unknown',
      email: email || '',
      phoneNumber: phone || '',
    };
    
    return {
      // legacy keys used by UI
      id: b.id,
      assigned_to: b.assigned_to ?? b.assignedTo?.id ?? b.assignee_id ?? b.assigned_id ?? b.assignedId,
      created_by: b.created_by ?? b.createdBy?.id ?? b.created_by_id ?? b.createdById,
      query_id: b.query_id ? Number(b.query_id) : undefined,
      parent_id: b.parent_id || null,
      status: b.status || '—',
      payment_status: b.payment_status || '',
      created_at: b.created_at,
      updated_at: b.updated_at,
      travel_start_date: b.start_at,
      travel_end_date: b.end_at,
      total_price: totalPrice,
      currency: b.currency || 'INR',
      product_type: b.product_type || '',
      product: b.product || {},
      booking_plan: b.booking_plan,
      pdf_url: b.pdf_url || '',
      voucher_url: b.voucher_url || '',
      extras: b.extras || {},
      draft_id: b.draft_id || null,
      // enrich for UI search and display
      package: { title: b.title || (b.product?.hotel?.name) || 'Booking' },
      customer: customer,
      payments: [], // Payments will be loaded separately when viewing history
      // Store payment_details for reference
      payment_details: b.payment_details,
    } as any;
  });
  return mapped;
};

export async function assignBooking(bookingId: number | string, userId: number): Promise<{ id: number | string }> {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}/assign`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({ "assigned_to": userId })
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to assign booking: ${errorText}`);
  }

  return res.json();
}

export async function addPayment(bookingId: number | string, paymentData: { amount: number; method: string; transactionId: string; notes?: string; }): Promise<{ id: number | string }> {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}/payments`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(paymentData)
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to add payment: ${errorText}`);
  }

  return res.json();
}

export async function paymentReminder(bookingId: number | string): Promise<{ id: number | string }> {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}/payments/reminder`, {
    method: 'POST',
    headers: getApiHeaders('json'),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to send payment reminder: ${errorText}`);
  }

  return res.json();
}

export interface CreateBookingPayload {
  productType: 'hotel' | 'flight' | 'activity' | 'package' | 'transfer' | 'other';
  provider: string;
  amountPaise: number;
  currency: string;
  title?: string;
  status?: string;
  paymentStatus?: string;
  parentId?: string | number;
  startAt?: string;
  endAt?: string;
  pricing?: any;
  product?: any;
  offer?: any;
  travellers?: any;
  contact?: any;
  extras?: any;
  providerRef?: any;
  providerConfirmation?: string;
  queryId?: number | string;
  customerId?: number | string;
  createdBy?: string;
  assigneeId?: string;
}

export async function createBooking(payload: CreateBookingPayload) {
  const res = await fetch(`${API_BASE_URL}/bookings`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(toSnakeCase(payload)),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to create booking: ${errorText}`);
  }
  return res.json();
}

export async function createBookingFromQuery(
  queryId: number,
  payload?: Partial<CreateBookingPayload> & {
    totalPrice?: number;
    travelStartDate?: string;
    travelEndDate?: string;
    notes?: string;
  }
) {
  const drafts = await fetchBookingDrafts(queryId);
  if (drafts.length > 0) {
    const sorted = drafts.slice().sort((a: any, b: any) => {
      const at = new Date(a.updated_at || a.created_at || 0).getTime() || 0;
      const bt = new Date(b.updated_at || b.created_at || 0).getTime() || 0;
      return bt - at;
    });
    const draft = sorted[0];
    const draftId = draft.id || draft.draft_id || draft.booking_id;
    if (draftId) {
      return materializeBookingDraft(draftId);
    }
  }

  const normalizedStatus = payload?.status ? payload.status.toUpperCase() : undefined;
  const status = normalizedStatus === 'BOOKED' ? 'BOOKING' : normalizedStatus;
  const amountPaise = typeof payload?.amountPaise === 'number'
    ? payload.amountPaise
    : typeof payload?.totalPrice === 'number'
      ? Math.max(0, Math.round(payload.totalPrice * 100))
      : 0;
  const extras = payload?.extras
    ? { ...payload.extras, ...(payload?.notes ? { notes: payload.notes } : {}) }
    : (payload?.notes ? { notes: payload.notes } : undefined);

  return createBooking({
    productType: payload?.productType || 'package',
    provider: payload?.provider || 'CRM',
    amountPaise,
    currency: payload?.currency || 'INR',
    title: payload?.title,
    status: status || 'BOOKING',
    paymentStatus: payload?.paymentStatus,
    parentId: payload?.parentId,
    startAt: payload?.startAt || payload?.travelStartDate,
    endAt: payload?.endAt || payload?.travelEndDate,
    pricing: payload?.pricing,
    product: payload?.product,
    offer: payload?.offer,
    travellers: payload?.travellers,
    contact: payload?.contact,
    extras,
    providerRef: payload?.providerRef,
    providerConfirmation: payload?.providerConfirmation,
    queryId,
    customerId: payload?.customerId,
    createdBy: payload?.createdBy,
    assigneeId: payload?.assigneeId,
  });
}

export async function updateBooking(bookingId: number | string, payload: Partial<CreateBookingPayload>) {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
    method: 'PUT',
    headers: getApiHeaders('json'),
    body: JSON.stringify(toSnakeCase(payload)),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to update booking: ${errorText}`);
  }
  return res.json();
}

export async function fetchBookingById(bookingId: number | string): Promise<any> {
  const res = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
    headers: getApiHeaders('none'),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Failed to fetch booking: ${errorText}`);
  }
  return res.json();
}
